import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useHasMenu } from '../../../auth/access';
import { ConfirmationModal } from '../../shared/components/feedback';
import { errorText, Group, groupsApi, groupTree, natureLabel } from '../accounting.api';
import BooksHeader from '../components/BooksHeader';
import { useBooksCompany } from '../components/CompanySelect';
import GroupDialog, { GroupTarget } from '../components/GroupDialog';

const NATURE_COLOR = { ASSET: 'info', LIABILITY: 'warning', INCOME: 'success', EXPENSE: 'error' } as const;

/**
 * The group tree: Tally's reserved groups (fixed) and the company's own under them or beside
 * them. Sub-groups take their parent's nature and cash / bank kind.
 */
export default function GroupsPage() {
  const { companies, companyId, setCompanyId, loaded } = useBooksCompany();
  const canEdit = useHasMenu('accounts.groups', 'EDIT');

  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [target, setTarget] = useState<GroupTarget | null>(null);
  const [deleting, setDeleting] = useState<Group | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    groupsApi
      .list(companyId)
      .then(setGroups)
      .catch((e) => setError(errorText(e, 'Could not load groups', 'ACCOUNTING_GROUPS_FAILED')))
      .finally(() => setLoading(false));
  }, [companyId]);

  useEffect(load, [load]);

  const tree = useMemo(() => groupTree(groups), [groups]);
  const hasKids = useMemo(() => new Set(groups.map((g) => g.parentId).filter((x): x is number => x !== null)), [groups]);

  // Hide everything under a collapsed group.
  const visible = useMemo(() => {
    const hidden = new Set<number>();
    return tree.filter(({ group }) => {
      if (group.parentId && (hidden.has(group.parentId) || collapsed.has(group.parentId))) {
        hidden.add(group.id);
        return false;
      }
      return true;
    });
  }, [tree, collapsed]);

  const toggle = (id: number) =>
    setCollapsed((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const remove = async () => {
    if (!companyId || !deleting) return;
    setBusy(true);
    try {
      await groupsApi.remove(companyId, deleting.id);
      toast.success(`${deleting.name} deleted.`);
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(errorText(e, 'Could not delete the group', 'ACCOUNTING_GROUP_DELETE_FAILED'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap={3}>
      <BooksHeader
        title='LEDGER GROUPS'
        companies={companies}
        companyId={companyId}
        onCompany={setCompanyId}
        loaded={loaded}
        action={
          <>
            <Button
              size='small'
              onClick={() => setCollapsed(collapsed.size ? new Set() : new Set(hasKids))}
              startIcon={<Icon icon={collapsed.size ? 'mdi:unfold-more-horizontal' : 'mdi:unfold-less-horizontal'} />}
            >
              {collapsed.size ? 'Expand all' : 'Collapse all'}
            </Button>
            {canEdit && (
              <Button variant='contained' startIcon={<Icon icon='mdi:plus' />} disabled={!companyId} onClick={() => setTarget({ group: null, parentId: null })}>
                Primary group
              </Button>
            )}
          </>
        }
      />

      {loading && <LinearProgress />}
      {error && <Alert severity='error'>{error}</Alert>}

      {companyId && (
        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Group</TableCell>
                <TableCell>Nature</TableCell>
                <TableCell align='right'>Ledgers</TableCell>
                {canEdit && <TableCell align='right' />}
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map(({ group: g, depth }) => (
                <TableRow key={g.id} hover>
                  <TableCell sx={{ minWidth: 220 }}>
                    <Stack direction='row' alignItems='center' gap={0.5} sx={{ pl: depth * 2.5 }}>
                      {hasKids.has(g.id) ? (
                        <IconButton size='small' onClick={() => toggle(g.id)} sx={{ p: 0.25 }}>
                          <Icon icon={collapsed.has(g.id) ? 'mdi:chevron-right' : 'mdi:chevron-down'} />
                        </IconButton>
                      ) : (
                        <Box sx={{ width: 26 }} />
                      )}
                      <Typography variant='body2' fontWeight={depth === 0 ? 700 : 500}>
                        {g.name}
                      </Typography>
                      {g.cashBank && <Chip size='small' variant='outlined' color='primary' label={g.cashBank === 'CASH' ? 'Cash' : 'Bank'} />}
                      {g.system && (
                        <Tooltip title='Reserved group: cannot be renamed, moved or deleted'>
                          <Chip size='small' label='System' icon={<Icon icon='mdi:lock-outline' />} />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip size='small' variant='outlined' color={NATURE_COLOR[g.nature]} label={natureLabel(g.nature)} />
                  </TableCell>
                  <TableCell align='right'>{g.ledgerCount || ''}</TableCell>
                  {canEdit && (
                    <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title='Add sub-group'>
                        <IconButton size='small' onClick={() => setTarget({ group: null, parentId: g.id })}>
                          <Icon icon='mdi:folder-plus-outline' />
                        </IconButton>
                      </Tooltip>
                      {!g.system && (
                        <>
                          <Tooltip title='Rename / move'>
                            <IconButton size='small' onClick={() => setTarget({ group: g, parentId: g.parentId })}>
                              <Icon icon='mdi:pencil' />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title='Delete'>
                            <span>
                              <IconButton size='small' color='error' disabled={g.ledgerCount > 0 || hasKids.has(g.id)} onClick={() => setDeleting(g)}>
                                <Icon icon='mdi:delete-outline' />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <GroupDialog target={target} companyId={companyId} groups={groups} onClose={() => setTarget(null)} onSaved={load} />

      <ConfirmationModal
        open={deleting !== null}
        title={`Delete ${deleting?.name ?? ''}?`}
        message='Only an empty group (no sub-groups, no ledgers) can be deleted.'
        confirmText='Delete'
        onConfirm={remove}
        onClose={() => setDeleting(null)}
        loading={busy}
      />
    </Stack>
  );
}
